import React, { useEffect, useState } from "react";
import { t } from "ttag";
import { BaseDialog, TextField, Typography } from "@coolremote/shared";
import type { ModbusCustomEnum } from "../types";
import styles from "./BinaryMapDialog.module.css";

interface IBinaryMapDialogProps {
  open: boolean;
  paramName?: string;
  value?: ModbusCustomEnum;
  /** When true, value 0 is the active/on state (reverse binary). Labels swap accordingly. */
  isReverseBinary?: boolean;
  onClose: () => void;
  onSave: (value: ModbusCustomEnum) => void;
  readOnly?: boolean;
}

const BinaryMapDialog: React.FC<IBinaryMapDialogProps> = ({ open, paramName, value, isReverseBinary, onClose, onSave, readOnly }) => {
  const [trueLabel, setTrueLabel] = useState("");
  const [falseLabel, setFalseLabel] = useState("");

  useEffect(() => {
    if (open) {
      // Default the two states to On/Off (value 1 = On, value 0 = Off for
      // plain binary; reversed for reverse binary where 0 is the active state).
      setTrueLabel(value?.["1"] ?? (isReverseBinary ? t`Off` : t`On`));
      setFalseLabel(value?.["0"] ?? (isReverseBinary ? t`On` : t`Off`));
    }
  }, [open, value, isReverseBinary]);

  const handleSave = () => {
    onSave({ "1": trueLabel, "0": falseLabel });
  };

  // Binary: true=1, false=0. Reverse binary: true=0, false=1.
  const trueHint = isReverseBinary ? t`True — value = 0` : t`True — value = 1`;
  const falseHint = isReverseBinary ? t`False — value = 1` : t`False — value = 0`;

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={paramName ? `${t`Binary Map`} — ${paramName}` : t`Binary Map`}
      onConfirm={readOnly ? undefined : handleSave}
      hideConfirm={readOnly}
      confirmLabel={t`Save`}
      cancelLabel={readOnly ? t`Close` : t`Cancel`}
      confirmSize="small"
      cancelSize="small"
      size="xs"
    >
      <div className={styles.content}>
        {!readOnly && (
          <Typography component="p" className={styles.hint}>
            {t`Define labels for the two binary states.`}
          </Typography>
        )}
        <TextField
          label={trueHint}
          fullWidth
          disabled={readOnly}
          value={trueLabel}
          onChange={(v: string) => setTrueLabel(v)}
          placeholder={t`e.g. On`}
        />
        <TextField
          label={falseHint}
          fullWidth
          disabled={readOnly}
          value={falseLabel}
          onChange={(v: string) => setFalseLabel(v)}
          placeholder={t`e.g. Off`}
        />
      </div>
    </BaseDialog>
  );
};

export default BinaryMapDialog;
